/**
 * @file AI Gallery「发布为网页」— 常量与类型定义（与 API 接口规范一致）。
 * 
 * API 字段规范：
 * - name_cn: string(1-40) 作品中文名（必填）
 * - name_en: string(1-60) 作品英文名（必填）
 * - desc_cn: string(0-200) 中文描述
 * - desc_en: string(0-300) 英文描述
 * - source: enum (link / file) 与提交形态严格匹配（必填）
 * - link: string(URL) source=link 时必填，必须 https://，长度 ≤ 2048
 * - tags: TagRef[] 1-5 个标签，元素 {id: 1..9}（必填）
 * - visibility: enum (self / user / public)（必填）
 * - share_users: string[] visibility=user 时至少 1 个 openid
 * - file: binary 仅 multipart 模式；ZIP 或 HTML 二进制
 */

// ==============================
// 常量定义
// ==============================

/**
 * 最大标签数量限制
 */
export const AI_GALLERY_PUBLISH_MAX_TAGS = 5;

// ==============================
// 类型定义
// ==============================

/**
 * 访问权限枚举类型
 * - self: 仅自己可见
 * - user: 指定人员可见
 * - public: 公开可见
 */
export type TAiGalleryPublishVisibility = 'self' | 'user' | 'public';

/**
 * 数据类型枚举类型
 * - real: 真实数据
 * - demo: Demo 数据
 */
export type TAiGalleryDataMode = 'real' | 'demo';

/**
 * 分享用户信息接口（用于表单内部状态）
 */
export interface IAiGalleryShareUserInfo {
  /** 用户唯一标识 */
  openid: string;
  /** 用户名 */
  username: string;
  /** 邮箱（可选） */
  email?: string;
  /** 头像（可选） */
  avatar?: string;
}

/**
 * 访问权限载荷接口（表单内部使用，包含用户详情）
 */
export interface IAiGalleryPublishVisibilityPayload {
  /** 访问权限类型 */
  visibility: TAiGalleryPublishVisibility;
  /** 分享用户列表（包含显示信息） */
  shareUsers: IAiGalleryShareUserInfo[];
}

// ==============================
// 工具函数
// ==============================

/**
 * 规范化访问权限载荷
 */
export function normalizeAiGalleryPublishVisibilityPayload(
  value: unknown,
): IAiGalleryPublishVisibilityPayload {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'visibility' in value) {
    const v = value as IAiGalleryPublishVisibilityPayload;
    const visibility: TAiGalleryPublishVisibility = 
      v.visibility === 'self' || v.visibility === 'user' || v.visibility === 'public'
        ? v.visibility
        : 'self';
    return {
      visibility,
      shareUsers: Array.isArray(v.shareUsers) ? v.shareUsers : [],
    };
  }
  return {
    visibility: 'self',
    shareUsers: [],
  };
}


